// ─── Generation progress ──────────────────────────────────────────────────────
// How far along a running generation is, so the wait shows real movement instead
// of a spinner.
//
// The stream is the only signal there is: the generator emits a delimited
// document — ===TITLE===, the shared ===CSS===, ===ICON===, one ===PAGE <file>===
// per page, then ===JS=== (builderslab/server/services/generator.js) — and the
// server sends no progress events, so nothing knows the finished length in
// advance. The stylesheet leads deliberately: the preview applies it as soon as
// it lands, so the pages after it paint designed rather than as bare text.
// Two things turn that into an honest bar:
//
//  1. The page count is known long before the pages are written. Every page
//     carries the same nav and every internal link must resolve to a page the
//     model actually emits (MULTI_PAGE_RULES), so the hrefs in the first page's
//     header name the whole site while it is still on page one.
//  2. Progress never moves backwards. This module is pure and re-derives from
//     the whole stream each call; useGenerationProgress ratchets the result, so
//     a sharpened estimate can only ever push the bar forward.
//
// A refinement is the awkward case: the patch path buffers the model's edits and
// emits the finished document in a single chunk, so there is no stream to read
// at all. An edit is therefore its own mode, carried by a faster clock, with no
// build stages. Only if the server falls back to writing the site out again (it
// says so with a `rewriting` event) is there a stream, read like a build's.

import { DEFAULT_PAGE, normalizePagePath, pageLabel } from '@/lib/sitePages';

/** `===PAGE about.html===` on its own line. */
const PAGE_MARK_RE = /^[\t ]*={3,}[ \t]*PAGE[:\s][ \t]*([^\n=]+?)[ \t]*={3,}[\t ]*$/gm;
/** `===TITLE===`, `===CSS===`, … on their own lines. */
const KEY_MARK_RE = /^[\t ]*={3,}[ \t]*(TITLE|ICON|HTML|CSS|JS)[ \t]*={3,}[\t ]*$/gm;
/** Internal links — the nav names the pages that are still to come. */
const HREF_RE = /href\s*=\s*["']([^"'\s>]+?\.html?)["']/gi;

// The prompt caps a site at six pages. A nav that links somewhere unexpected
// can't stretch the denominator past that and stall the bar.
const MAX_PAGES = 6;

// Where each section sits on the bar. The page band is the widest because
// writing the pages is the bulk of the work.
// The generator emits the stylesheet FIRST now (see the delimiter format in
// services/generator.js): the preview applies it the moment it lands, so
// everything after paints designed rather than as unstyled text. The bands
// follow that order, and the CSS band is wide because writing the design
// system up front is real work rather than a trailing detail.
const BANDS = {
  connect: [1, 6],
  title: [6, 10],
  css: [10, 30],
  icon: [30, 36],
  pages: [36, 90],
  js: [90, 98],
};

// Chars of section text at which a phase is ~63% of the way through its band.
const EASE_CHARS = {
  connect: 200,
  title: 120,
  icon: 500,
  page: 4000,
  css: 3000,
  js: 2000,
};

/** Approach `hi` from `lo` without ever reaching it, as `x` grows. */
function ease([lo, hi], x, k) {
  return lo + (hi - lo) * (1 - Math.exp(-Math.max(0, x) / k));
}

/**
 * The elapsed-time floor. The model can be silent for a long stretch — prompt
 * enhancement runs before the first token, and a patched refinement streams
 * nothing until it is finished — so the bar leans on the clock when it has no
 * text to read. It approaches, and never reaches, the end of the JS band.
 */
function timeFloor(elapsedMs) {
  return 92 * (1 - Math.exp(-Math.max(0, elapsedMs) / 55000));
}

/**
 * The clock for an edit. A patched edit streams nothing until it is applied,
 * usually inside half a minute, so the build's floor (tuned for a run of
 * several minutes) would sit near a fifth of the way along for all of it.
 */
function editFloor(elapsedMs) {
  return 90 * (1 - Math.exp(-Math.max(0, elapsedMs) / 20000));
}

/**
 * Everything the delimited stream says about its own state.
 * Exported for the tests; callers want readGenerationProgress.
 */
export function readStreamState(text) {
  const src = String(text || '');
  const marks = [];
  let m;

  PAGE_MARK_RE.lastIndex = 0;
  while ((m = PAGE_MARK_RE.exec(src)) !== null) {
    marks.push({ kind: 'page', path: normalizePagePath(m[1]), at: m.index + m[0].length });
  }

  KEY_MARK_RE.lastIndex = 0;
  while ((m = KEY_MARK_RE.exec(src)) !== null) {
    marks.push({ kind: m[1].toLowerCase(), at: m.index + m[0].length });
  }

  marks.sort((a, b) => a.at - b.at);

  const started = [];
  // Which fixed sections have actually appeared. Inferring "the stylesheet is
  // written" from the current phase claims a file the stream never emitted.
  const seen = new Set();
  let last = null;

  for (const mark of marks) {
    // 'html' is the home page under its pre-multi-page name.
    if (mark.kind === 'page' || mark.kind === 'html') {
      const path = mark.kind === 'page' ? mark.path : DEFAULT_PAGE;
      if (!started.includes(path)) started.push(path);
      last = { key: 'pages', path, at: mark.at };
    } else {
      seen.add(mark.kind);
      last = { key: mark.kind, at: mark.at };
    }
  }

  // Once the shared JS has started, no more pages are coming — the count the
  // stream has already shown is the real one, whatever the nav promised.
  // Deliberately not 'css': the stylesheet is emitted FIRST now, so its marker
  // means the pages have not begun, which is the opposite of closed.
  const pagesClosed = !!last && last.key === 'js';

  let expectedPages = started.length;
  // Wait for the nav to close before counting its links. Reading it half-written
  // walks the total up one link at a time — "page 1 of 2", "of 3", "of 4" — which
  // reads as the estimate flailing rather than sharpening.
  if (!pagesClosed && /<\/nav>|<\/header>/i.test(src)) {
    const linked = new Set(started);
    HREF_RE.lastIndex = 0;
    while ((m = HREF_RE.exec(src)) !== null) {
      const raw = m[1].trim();
      // An absolute URL is someone else's site, not a page we are waiting for.
      if (/^[a-z]+:\/\//i.test(raw) || raw.startsWith('//')) continue;
      linked.add(normalizePagePath(raw));
    }
    expectedPages = Math.min(Math.max(linked.size, started.length), MAX_PAGES);
  }

  return {
    phaseKey: last?.key ?? 'connect',
    currentPage: last?.key === 'pages' ? last.path : '',
    pages: started,
    expectedPages: Math.max(expectedPages, started.length, 1),
    seenCss: seen.has('css'),
    seenJs: seen.has('js'),
    // Text written since the current section opened — how far into it we are.
    sinceMark: last ? src.length - last.at : src.length,
    length: src.length,
  };
}

/** How far through its own band the current section is. */
function structuralPercent(state) {
  const { phaseKey, sinceMark, length, pages, expectedPages } = state;

  switch (phaseKey) {
    case 'js':
      return ease(BANDS.js, sinceMark, EASE_CHARS.js);
    case 'css':
      return ease(BANDS.css, sinceMark, EASE_CHARS.css);
    case 'pages': {
      const [lo, hi] = BANDS.pages;
      // Every page but the one in flight is finished; that one eases toward
      // done on its own text, so the bar keeps moving mid-page.
      const finished = Math.max(0, pages.length - 1);
      const partial = 1 - Math.exp(-Math.max(0, sinceMark) / EASE_CHARS.page);
      const share = Math.min((finished + partial) / Math.max(expectedPages, 1), 1);
      return lo + (hi - lo) * share;
    }
    case 'icon':
      return ease(BANDS.icon, sinceMark, EASE_CHARS.icon);
    case 'title':
      return ease(BANDS.title, sinceMark, EASE_CHARS.title);
    default:
      return ease(BANDS.connect, length, EASE_CHARS.connect);
  }
}

/** The coarse stage, for the step pips. */
function stepOf(phaseKey) {
  if (phaseKey === 'pages') return 'pages';
  if (phaseKey === 'css') return 'css';
  if (phaseKey === 'js') return 'js';
  return 'plan';
}

function describe(state) {
  const { phaseKey, currentPage, pages, expectedPages } = state;

  switch (phaseKey) {
    case 'js':
      return { phase: 'Wiring up interactions', detail: 'Menus, forms, filters and carousels' };
    case 'css':
      return { phase: 'Designing the system', detail: 'Palette, type scale and spacing, before any markup' };
    case 'pages': {
      const n = pages.length;
      const total = Math.max(expectedPages, n);
      return {
        phase: `Writing ${pageLabel(currentPage || DEFAULT_PAGE)}`,
        detail: total > 1 ? `Page ${n} of ${total} · ${currentPage}` : currentPage,
      };
    }
    case 'icon':
      return { phase: 'Drawing the brand mark', detail: 'A logo and favicon drawn from the palette' };
    case 'title':
      return { phase: 'Naming your site', detail: 'Settling on a name and a direction' };
    default:
      return { phase: 'Understanding your idea', detail: 'Choosing the pages and the layout' };
  }
}

/**
 * An edit the server could not make as a patch, written out again whole. The
 * stream is a build's, but "Designing the system" and "Naming your site" would
 * describe a new site to someone who asked for one change.
 */
function describeRewrite(state) {
  const { phaseKey, currentPage, pages, expectedPages } = state;

  switch (phaseKey) {
    case 'js':
      return { phase: 'Rewriting the interactions', detail: 'Menus, forms, filters and carousels' };
    case 'css':
      return { phase: 'Rewriting the stylesheet', detail: 'Your palette, type and spacing, carried over' };
    case 'pages': {
      const n = pages.length;
      const total = Math.max(expectedPages, n);
      return {
        phase: `Rewriting ${pageLabel(currentPage || DEFAULT_PAGE)}`,
        detail: total > 1 ? `Page ${n} of ${total} · ${currentPage}` : currentPage,
      };
    }
    default:
      return { phase: 'Rewriting your site', detail: 'This change needs every page written out again' };
  }
}

/** Files the stream has finished writing, for the "Wrote …" chips. */
function filesWritten(state, isDone) {
  const { phaseKey, pages } = state;
  const out = [];

  // A page is written once something after it has started.
  const pastPages = phaseKey === 'pages' && !isDone ? pages.slice(0, -1) : pages;
  out.push(...pastPages);

  // style.css is written before the pages now, so it is done as soon as
  // anything after it has started — but only if the stream actually emitted it.
  if (isDone || (state.seenCss && phaseKey !== 'css')) out.unshift('style.css');
  if (isDone) out.push('script.js');

  return out;
}

/**
 * @param {string} text            the stream so far
 * @param {object} [options]
 * @param {number} [options.elapsedMs]     time since the request went out
 * @param {boolean} [options.isDone]       the run finished — pins the bar at 100
 * @param {boolean} [options.edit]         a change to a site that already exists
 * @param {boolean} [options.rewriting]    the server is writing that site out
 *                                         again, because a patch could not do it
 * @returns {{mode:'build'|'edit'|'rewrite', percent:number, stepKey:string,
 *            phaseKey:string, phase:string, detail:string, pages:string[],
 *            expectedPages:number, currentPage:string, filesDone:string[]}}
 */
export function readGenerationProgress(text, { elapsedMs = 0, isDone = false, edit = false, rewriting = false } = {}) {
  const mode = !edit ? 'build' : rewriting ? 'rewrite' : 'edit';
  const changesIn = { phase: 'Your changes are in', detail: 'Updated in the preview' };

  // An edit made as a patch: the server asks for the change alone and sends
  // the site back once it is applied, in one piece. There are no pages,
  // stylesheet or script being written in turn, so build stages here — "Plan",
  // "Designing the system" — narrated a rebuild nobody asked for.
  if (mode === 'edit') {
    return {
      mode,
      percent: isDone ? 100 : Math.min(99, Math.round(editFloor(elapsedMs))),
      phaseKey: isDone ? 'done' : 'edit',
      stepKey: isDone ? 'done' : 'edit',
      ...(isDone ? changesIn : { phase: 'Making your change', detail: 'Only what you asked for. The rest stays as it is' }),
      pages: [],
      expectedPages: 1,
      currentPage: '',
      filesDone: [],
    };
  }

  const state = readStreamState(text);
  const { phase, detail } = isDone
    ? (mode === 'rewrite' ? changesIn : { phase: 'Your site is ready', detail: 'Opening it in the preview' })
    : (mode === 'rewrite' ? describeRewrite(state) : describe(state));

  // The clock may run ahead of the text, but never past the end of the section
  // that is actually streaming — so the bar can't imply the stylesheet is being
  // written while the model is still on page two. Before the first delimiter
  // there is no section to bound it by, and that is exactly the case the floor
  // is for (a patched refinement streams nothing at all), so it runs free there.
  const ceiling = state.phaseKey === 'connect' ? 99 : BANDS[state.phaseKey]?.[1] ?? 99;
  const percent = isDone
    ? 100
    // Capped below 100 so the bar can't claim to be finished while it is not.
    : Math.min(99, Math.round(Math.max(
      structuralPercent(state),
      Math.min(timeFloor(elapsedMs), ceiling),
    )));

  return {
    mode,
    percent,
    phaseKey: isDone ? 'done' : state.phaseKey,
    stepKey: isDone ? 'done' : stepOf(state.phaseKey),
    phase,
    detail,
    pages: state.pages,
    expectedPages: state.expectedPages,
    currentPage: state.currentPage,
    filesDone: filesWritten(state, isDone),
  };
}

// ─── Task list ────────────────────────────────────────────────────────────────
// The step list showed five fixed stages, so "Writing 4 pages" stayed a single
// row for most of the run — the longest stretch of the wait was also the least
// legible. This expands the work into one row per page, which turns the panel
// into a checklist that visibly empties.
//
// An approved plan makes this properly useful: the pages are known before a
// single token is written, so the whole list is on screen from the start and
// each row is ticked as the stream reaches it. Without a plan the list is
// discovered from the stream instead, and fills in as the nav reveals what is
// coming.

const PHASE_ORDER = ['connect', 'title', 'css', 'icon', 'pages', 'js', 'done'];

/**
 * @param {object} progress  the value from readGenerationProgress
 * @param {object} [plan]    an approved plan, when the user went through one
 * @returns {Array<{key:string,label:string,detail:string,status:'done'|'active'|'pending'}>}
 */
export function buildTaskList(progress, plan = null) {
  const { phaseKey, pages = [], expectedPages = 1, currentPage = '' } = progress || {};
  const isDone = phaseKey === 'done';
  const reached = PHASE_ORDER.indexOf(phaseKey);
  const past = (key) => isDone || reached > PHASE_ORDER.indexOf(key);

  const status = (done, active) => (done ? 'done' : active ? 'active' : 'pending');

  const tasks = [{
    key: 'plan',
    label: plan ? 'Following your approved plan' : 'Planning structure & layout',
    detail: plan?.direction || 'Choosing the pages and the layout',
    status: status(past('connect'), !isDone && phaseKey === 'connect'),
  }, {
    key: 'css',
    label: 'Design the system',
    detail: 'Palette, type and spacing — settled before any markup',
    status: status(past('css'), !isDone && phaseKey === 'css'),
  }, {
    key: 'brand',
    label: 'Naming it and drawing the mark',
    detail: plan?.title || 'A logo and favicon drawn from the palette',
    status: status(past('icon'), !isDone && (phaseKey === 'title' || phaseKey === 'icon')),
  }];

  // The pages to list. A plan names them all upfront; otherwise use what the
  // stream has revealed, padded to the count the nav implies so the list does
  // not visibly grow a row at a time.
  const planned = (plan?.pages || []).map((p) => ({ path: p.path, label: p.label, sections: p.sections }));
  const discovered = pages.map((path) => ({ path, label: pageLabel(path), sections: null }));

  let list = planned.length ? planned : discovered;
  if (!planned.length) {
    for (let i = list.length; i < Math.max(expectedPages, 1); i++) {
      list = [...list, { path: '', label: 'Another page', sections: null }];
    }
  }

  for (const [i, page] of list.entries()) {
    // A page is finished once the stream has started something after it.
    const started = page.path ? pages.includes(page.path) : i < pages.length;
    const isCurrent = phaseKey === 'pages' && !isDone &&
      (page.path ? currentPage === page.path : i === pages.length - 1);
    const done = past('pages') || (started && !isCurrent);

    tasks.push({
      key: `page:${page.path || i}`,
      label: `Write ${page.label || page.path}`,
      detail: page.sections?.length ? page.sections.join(' · ') : (page.path || 'Building DOM elements'),
      status: status(done, isCurrent),
    });
  }

  tasks.push({
    key: 'js',
    label: 'Wire up interactions',
    detail: 'Menus, forms, filters and carousels',
    status: status(past('js'), !isDone && phaseKey === 'js'),
  }, {
    key: 'finish',
    label: 'Final pass',
    detail: 'Closing tags and checking every link resolves',
    status: status(isDone, false),
  });

  return tasks;
}

/** How many task rows are complete — for the "3 of 8" counter. */
export function taskProgress(tasks) {
  const done = tasks.filter((t) => t.status === 'done').length;
  return { done, total: tasks.length };
}

/** Milliseconds as `m:ss`, for the elapsed counter. */
export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Milliseconds as "42s", "6m 45s" or "1h 3m" — how long a finished run took. */
export function formatDuration(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${m}m`;
  return s ? `${m}m ${s}s` : `${m}m`;
}
